import { Box, CircularProgress, Divider, LinearProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import AgeRangeStep from './AgeRangeStep';
import NamingQuestionStep from './NamingQuestionStep';
import NamingSuggestionFinal from './NamingSuggestionFinal';
import { ProductNameSuggestionWizard } from './useProductNameSuggestionWizard';

interface NamingWizardProps extends ProductNameSuggestionWizard {
  // renders a "Précédent" button next to the question's actions and on the final suggestion
  onBack?: () => void;
}

export default function NamingWizard({
  status,
  currentStep,
  suggestedName,
  pendingAgeRange,
  progress,
  acceptSuggestion,
  confirmAgeRange,
  selectCandidate,
  keepOriginal,
  onBack,
}: NamingWizardProps): React.JSX.Element {
  const { t } = useTranslation(['products']);

  const isBuilding = status === 'building';

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
        <CircularProgress />
        <Typography variant="bodyMedium">{t('products:naming_wizard.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {(status === 'question' || isBuilding) && currentStep && (pendingAgeRange ? (
        <AgeRangeStep
          question={pendingAgeRange.followUpQuestion ?? ''}
          isLoading={isBuilding}
          onConfirm={confirmAgeRange}
          onBack={onBack}
        />
      ) : (
        <NamingQuestionStep
          key={currentStep.slot}
          step={currentStep}
          isLoading={isBuilding}
          onSelectCandidate={selectCandidate}
          onBack={onBack}
        />
      ))}
      {status === 'final' && (
        <NamingSuggestionFinal
          suggestedName={suggestedName}
          onAccept={acceptSuggestion}
          onKeepOriginal={keepOriginal}
          onBack={onBack}
        />
      )}
      {status !== 'final' && (
        <>
          <Divider />
          <LinearProgress
            variant="determinate"
            value={progress * 100}
            sx={{
              height: 8,
              borderRadius: '100px',
              backgroundColor: 'secondaryContainer.main',
              '& .MuiLinearProgress-bar': {
                borderRadius: '100px',
              },
            }}
          />
        </>
      )}
    </Box>
  );
}
